import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WarningOctagon, XCircle, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function Alerts() {
  const { user } = useAuth();
  const canResolve = user?.permissions?.includes("alert:resolve");
  const [alerts, setAlerts] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async (resolved = showResolved) => {
    setLoading(true);
    const { data } = await api.get("/alerts", { params: { include_resolved: resolved } });
    setAlerts(data);
    setLoading(false);
  };
  useEffect(() => { load(showResolved); /* eslint-disable-next-line */ }, [showResolved]);

  const resolve = async (id) => {
    try {
      await api.post(`/alerts/${id}/resolve`);
      toast.success("Alert resolved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <PageHeader title="Alerts" subtitle="Low-stock and out-of-stock notifications, also emailed to you">
        <div className="flex items-center gap-2">
          <Switch id="show-resolved" checked={showResolved} onCheckedChange={setShowResolved} data-testid="toggle-resolved" />
          <Label htmlFor="show-resolved" className="text-sm text-muted-foreground">Show resolved</Label>
        </div>
      </PageHeader>

      <div className="p-6 md:p-8">
        {loading ? (
          <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Loading alerts…</div>
        ) : alerts.length === 0 ? (
          <div className="bg-card border border-border rounded-md p-12 text-center" data-testid="alerts-empty">
            <span className="h-14 w-14 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto">
              <ShieldCheck size={30} weight="bold" />
            </span>
            <h3 className="text-lg font-heading font-medium mt-4">All clear</h3>
            <p className="text-sm text-muted-foreground mt-1">No active stock alerts. You'll be notified here and by email when items run low.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="alerts-list">
            {alerts.map((a) => {
              const out = a.level === "out";
              const Icon = out ? XCircle : WarningOctagon;
              return (
                <div key={a.id} data-testid={`alert-${a.id}`} className={`bg-card border rounded-md p-4 flex items-center gap-4 ${a.resolved ? "opacity-60 border-border" : out ? "border-destructive/40" : "border-warning/40"}`}>
                  <span className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${out ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning"}`}>
                    <Icon size={20} weight="bold" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{a.item_name}</p>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${out ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning"}`}>
                        {out ? "Out of stock" : "Low stock"}
                      </span>
                      {a.resolved && <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-success/10 text-success">Resolved</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 tnums">
                      {a.quantity} {a.unit} in stock · min {a.threshold} · {timeAgo(a.created_at)}
                    </p>
                  </div>
                  {!a.resolved && canResolve && (
                    <Button size="sm" variant="outline" data-testid={`resolve-alert-${a.id}`} onClick={() => resolve(a.id)} className="gap-1.5 shrink-0">
                      <CheckCircle size={15} /> Resolve
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
